import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Mail, Image, MessageSquare, TrendingUp, Clock, Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoadError(""); setLoading(true);
    axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setStats(r.data); setLoading(false); })
      .catch(() => {setLoadError("Dashboard data could not be loaded."); setLoading(false);});
  }, [token, reload]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const statCards = stats ? [
    { label: "Total Leads", value: stats.total_leads, icon: Users, color: "bg-blue-50 text-blue-600", href: "/admin/leads" },
    { label: "New Leads", value: stats.new_leads, icon: TrendingUp, color: "bg-green-50 text-green-600", href: "/admin/leads" },
    { label: "Won Leads", value: stats.won_leads, icon: TrendingUp, color: "bg-amber-50 text-amber-600", href: "/admin/leads" },
    { label: "Gallery Photos", value: stats.gallery_count, icon: Image, color: "bg-purple-50 text-purple-600", href: "/admin/gallery" },
  ] : [];

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    contacted: "bg-yellow-100 text-yellow-700",
    quoted: "bg-orange-100 text-orange-700",
    won: "bg-green-100 text-green-700",
    lost: "bg-gray-100 text-gray-600"
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Dashboard</h1>
        <p className="text-sm text-gray-500">Welcome back! Here's your business overview.</p>
      </div>

      {loadError && <div role="alert"><p>{loadError}</p><Button onClick={()=>setReload(n=>n+1)}>Retry loading dashboard</Button></div>}
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link key={card.label} to={card.href}>
              <Card className="border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{card.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{card.label}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Recent Leads */}
      <Card className="border border-gray-100">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Recent Leads</h2>
            <Link to="/admin/leads">
              <Button variant="ghost" size="sm" className="text-xs gap-1">View All <ArrowRight className="w-3 h-3" /></Button>
            </Link>
          </div>
          {loadError ? <p>Recent leads unavailable. Retry to load your inquiries.</p> : !stats?.recent_leads?.length ? (
            <p className="text-sm text-gray-500 text-center py-8">No leads yet. Share your website to start getting inquiries!</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_leads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#97BF04]/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-[#2C4001]">{lead.full_name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0D0D0D]">{lead.full_name}</p>
                      <p className="text-xs text-gray-500">{lead.service_needed}{lead.service_area ? ` - ${lead.service_area}` : ''}</p>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <Badge className={`text-xs ${statusColors[lead.status] || statusColors.new}`}>{lead.status}</Badge>
                    <a href={`tel:${lead.phone}`} className="text-[#97BF04] hover:text-[#2C4001]">
                      <Phone className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
